import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/home";

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const timerRef = useRef<number | null>(null);
  const count = heroSlides.length;

  const goTo = useCallback((next: number) => {
    setIndex((next + count) % count);
  }, [count]);

  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  useEffect(() => {
    timerRef.current = window.setInterval(() => {
      setIndex((prevIndex) => (prevIndex + 1) % count);
    }, 5000);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [count]);

  const slide = heroSlides[index];

  return (
    <section className="relative w-full h-[460px] md:h-[620px] overflow-hidden bg-foreground-950">
      {heroSlides.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
          aria-hidden={i !== index}
        >
          <img
            src={item.image}
            alt={`${item.title} 焦点图`}
            title={`${item.title} - po18 娱乐推荐`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/80 via-foreground-950/50 to-foreground-950/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent" />

      <div className="relative h-full w-full px-4 md:px-6 flex items-center">
        <div className="w-full max-w-2xl">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs md:text-sm font-semibold">
            <i className="ri-fire-line" />
            {slide.tag}
          </span>
          <h1 className="mt-4 text-3xl md:text-5xl font-black text-background-50 leading-tight">
            {slide.title}
          </h1>
          <p className="mt-4 text-sm md:text-base text-background-100/90 leading-relaxed max-w-xl">
            {slide.subtitle}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${slide.mediaId}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-circle-line text-lg" />
              查看详情
            </Link>
            <Link
              to="/news"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-50/15 text-background-50 text-sm font-semibold border border-background-50/30 hover:bg-background-50/25 transition-colors cursor-pointer whitespace-nowrap backdrop-blur"
            >
              <i className="ri-newspaper-line text-lg" />
              最新资讯
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-5 md:bottom-8 left-4 md:left-6 flex items-center gap-2">
        {heroSlides.map((item, i) => (
          <button
            key={item.id}
            type="button"
            onClick={() => goTo(i)}
            aria-label={`切换到第 ${i + 1} 张`}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? "w-8 bg-primary-500" : "w-3 bg-background-50/50 hover:bg-background-50/80"
            }`}
          />
        ))}
      </div>

      <div className="absolute bottom-5 md:bottom-8 right-4 md:right-6 flex items-center gap-2">
        <button
          type="button"
          onClick={prev}
          aria-label="上一张"
          className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-background-50/15 border border-background-50/30 text-background-50 flex items-center justify-center hover:bg-background-50/25 transition-colors cursor-pointer backdrop-blur"
        >
          <i className="ri-arrow-left-s-line text-xl" />
        </button>
        <button
          type="button"
          onClick={next}
          aria-label="下一张"
          className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-background-50/15 border border-background-50/30 text-background-50 flex items-center justify-center hover:bg-background-50/25 transition-colors cursor-pointer backdrop-blur"
        >
          <i className="ri-arrow-right-s-line text-xl" />
        </button>
      </div>
    </section>
  );
}